<template>
    <div class="grid-wrapper">
        <DxDataGrid ref="grid" :data-source="dxDataSource" @selection-changed="onSelectionChanged"
            @exporting="onExporting" v-bind="getDefaultGridProperties()">
            <DxSelection mode="single" />
            <DxHeaderFilter :visible="true" />
            <DxFilterRow :visible="true" />
            <DxFilterPanel :visible="true" />
            <DxPaging :enabled="true" :pageSize="10" />
            <DxPager :visible="true" :allowed-page-sizes="[10, 20, 50, 'all']" display-mode="full"
                :show-page-size-selector="true" :show-info="true" :show-navigation-buttons="true" />
            <DxRemoteOperations :filtering="true" :paging="true" :sorting="true" :summary="true" :grouping="true"
                :group-paging="true" />
            <DxExport :enabled="true" />
            <DxColumnChooser :enabled="true" />
            <DxToolbar>
                <DxItem location="after" :caption="$t('instalaciones.grid.toolbar.editar_instalacion')"
                    template="btnEditarInstalacion" v-if="tienePermisoInstalacionesEditar(autho)" />
                <DxItem location="after" :caption="$t('instalaciones.grid.toolbar.actualizar_rejilla')"
                    template="btnActualizarRejilla" />
                <DxItem location="after" :caption="$t('instalaciones.grid.toolbar.restaurar_rejilla')"
                    template="btnRestaurarRejilla" />
                <DxItem location="after" name="exportButton" />
                <DxItem location="after" name="columnChooserButton" />
            </DxToolbar>
            <DxColumn data-field="NombreCompleto" :caption="$t('instalaciones.grid.nombre')" sortOrder="asc"
                :sortIndex="0">
            </DxColumn>
            <DxColumn data-field="NombreInstalacionTipo" :caption="$t('instalaciones.grid.nombre_instalacion_tipo')"
                sortOrder="asc" :sortIndex="1">
            </DxColumn>
            <DxColumn data-field="NombreProvincia" :caption="$t('instalaciones.grid.nombre_provincia')">
            </DxColumn>
            <DxColumn data-field="NombreComunidadAutonoma"
                :caption="$t('instalaciones.grid.nombre_comunidad_autonoma')">
            </DxColumn>
            <DxColumn data-field="NombreZona" :caption="$t('instalaciones.grid.nombre_zona')">
            </DxColumn>
            <DxColumn data-field="Latitud" :caption="$t('instalaciones.grid.latitud')">
            </DxColumn>
            <DxColumn data-field="Longitud" :caption="$t('instalaciones.grid.longitud')">
            </DxColumn>
            <template #btnEditarInstalacion>
                <DxButton :hint="$t('instalaciones.grid.toolbar.editar_instalacion')" icon="fa fa-pencil"
                    styling-mode="text" type="default" @click="editarInstalacion"
                    :disabled="!hayUnaInstalacionSeleccionada" />
            </template>
            <template #btnActualizarRejilla>
                <DxButton :hint="$t('instalaciones.grid.toolbar.actualizar_rejilla')" @click="refresh(grid!.instance!)"
                    icon="fa-solid fa-sync" styling-mode="text" type="default" />
            </template>
            <template #btnRestaurarRejilla>
                <DxButton :hint="$t('instalaciones.grid.toolbar.restaurar_rejilla')" @click="reset(grid!.instance!)"
                    icon="fa-solid fa-filter-slash" styling-mode="text" type="default" />
            </template>
        </DxDataGrid>
    </div>
</template>

<script setup lang="ts">
import {
    DxAsyncRule, DxColumn, DxDataGrid, DxEditing, DxEmailRule, DxFormItem, DxRemoteOperations, DxRequiredRule, DxLookup, DxHeaderFilter, DxFilterRow,
    DxFilterPanel, DxPaging, DxPager, DxCustomRule, DxToolbar, DxItem, DxPopup, DxSelection, DxExport, type DxDataGridTypes, DxColumnChooser
} from "devextreme-vue/data-grid";
import { computed, ref } from 'vue';
import { useIntl } from '@services/i18n';
import { DxButton } from "devextreme-vue/button";
import { useApi } from '@/.shared/.api/integration';
import type { EditInstalacionModel, InstalacionDetalleModel } from '@/.shared/.api';
import type CustomStore from 'devextreme/data/custom_store';
import SaveInstalacionModal from "./SaveInstalacionModal.vue";
import { useRouter } from '@/.shared/services/routing-services';
import { useModalService } from "@/.shared/services/modal-service";
import type { RowDblClickEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { refresh, reset, exportToExcel, getDefaultGridProperties, getSelectedRow } from "@/.shared/helpers/grid-helper";
import type { EditInstalacionModalModel } from "./Model";
import { tienePermisoInstalacionesEditar } from "@/.shared/helpers/permission-helper";
import { useAuthorization } from "@/.shared/services/auth/authorization";

const props = defineProps<{ dxDataSourceProp: CustomStore | undefined }>();
const dxDataSource = computed(() => props.dxDataSourceProp);

const api = useApi();
const intl = useIntl();
const router = useRouter();
const autho = useAuthorization();
const modalService = useModalService();

const grid = ref<DxDataGrid>();
let instalacionSeleccionada = ref<InstalacionDetalleModel>();
let idInstalacionSeleccionada = computed(() => instalacionSeleccionada.value?.Id); // computed = function que devuelve algo. watch = function que no devuelve nada (void).
let hayUnaInstalacionSeleccionada = computed(() => instalacionSeleccionada.value != null);

function onSelectionChanged(e: SelectionChangedEvent): void {
    // const idInstalacionSeleccionada = e.selectedRowKeys[0];
    instalacionSeleccionada.value = e.selectedRowsData[0];
}

function onExporting(e: DxDataGridTypes.ExportingEvent): void {
    exportToExcel(e, 'Instalaciones');
}

async function editarInstalacion(): Promise<void> {
    if (hayUnaInstalacionSeleccionada.value) {
        const instalacion = instalacionSeleccionada.value!;
        const instalacionEditModel: EditInstalacionModalModel = {
            Id: instalacion.Id,
            Nombre: instalacion.Nombre,
            NombreInstalacionTipo: instalacion.NombreInstalacionTipo,
            NombreProvincia: instalacion.NombreProvincia,
            NombreComunidadAutonoma: instalacion.NombreComunidadAutonoma,
            Latitud: instalacion.Latitud,
            Longitud: instalacion.Longitud,
            IdZona: instalacion.IdZona
        };
        const result = await modalService.showModal(SaveInstalacionModal, { instalacionProp: instalacionEditModel }, { title: intl.i18n.t('instalaciones.grid.boton_editar_instalacion'), maxWidth: "600", maxHeight: "70vh" });
        if (result) {
            const dxDataGrid = grid.value!.instance!;
            await refresh(dxDataGrid);
            const instalacion = await getSelectedRow(dxDataGrid) as InstalacionDetalleModel;
            instalacionSeleccionada.value = instalacion;
        }
    }
}
</script>